import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { WarningIcon, CheckCircleIcon, TrashIcon } from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const REASONS = [
    "Privacy concerns",
    "Created a new account",
    "Too much screen time",
    "Not finding it useful",
    "Switching to another platform",
    "Other",
];

export default function DeleteAccount() {
    const [form, setForm] = useState({
        email: "",
        username: "",
        reason: "",
        confirm: false,
    });
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const set = (k) => (e) =>
        setForm((f) => ({
            ...f,
            [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        }));

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.confirm) {
            setErr("Please confirm that you want to permanently delete your account.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/account/delete-request", form);
            setSuccess(data.message);
            setForm({ email: "", username: "", reason: "", confirm: false });
        } catch (e2) {
            setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="px-5 md:px-10 max-w-3xl mx-auto py-16 md:py-24" data-testid="delete-account-page">
            <Seo title="Delete Account — India Reels" description="Request permanent deletion of your India Reels account and associated data." path="/delete-account" />
            <div className="text-xs uppercase tracking-[0.3em] text-secondary font-bold mb-3">
                account
            </div>
            <h1 className="font-heading font-black text-4xl md:text-6xl leading-tight">
                Delete your <span className="gradient-text">account</span>.
            </h1>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">
                Under Indian data protection law you have the right to delete your account and all
                associated data. Use this form to submit a deletion request — we process it within
                30 days.
            </p>

            <div className="mt-8 rounded-3xl border border-secondary/30 bg-secondary/5 p-5 md:p-6 flex gap-4 items-start" data-testid="delete-warning">
                <WarningIcon size={28} weight="fill" color="#FFA700" className="shrink-0 mt-0.5" />
                <div>
                    <div className="font-heading font-bold mb-1">This action is permanent</div>
                    <ul className="text-text-secondary text-sm space-y-1 list-disc pl-4">
                        <li>All your reels, comments, likes, follows will be removed</li>
                        <li>Your username will be released after 90 days</li>
                        <li>Any Creator Fund payouts pending will be processed first</li>
                        <li>You will not be able to recover any data once deletion completes</li>
                    </ul>
                </div>
            </div>

            {success ? (
                <div
                    className="mt-8 rounded-3xl border border-white/10 bg-surface p-8 text-center fade-up"
                    data-testid="delete-success"
                >
                    <div className="w-20 h-20 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-5">
                        <CheckCircleIcon size={46} weight="fill" color="#E01A4F" />
                    </div>
                    <h2 className="font-heading font-black text-2xl md:text-3xl">
                        Deletion request received
                    </h2>
                    <p className="text-text-secondary mt-3 max-w-md mx-auto">{success}</p>
                </div>
            ) : (
                <form
                    onSubmit={submit}
                    className="mt-8 rounded-3xl border border-white/10 bg-surface p-6 md:p-10 space-y-5"
                >
                    <Field
                        label="Account email"
                        testid="delete-email"
                        type="email"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="email used on India Reels"
                    />
                    <Field
                        label="Username (optional)"
                        testid="delete-username"
                        value={form.username}
                        onChange={set("username")}
                        placeholder="@yourusername"
                    />
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">
                            Reason (helps us improve)
                        </label>
                        <select
                            data-testid="delete-reason"
                            value={form.reason}
                            onChange={set("reason")}
                            className="input-field mt-2"
                        >
                            <option value="">Select a reason (optional)</option>
                            {REASONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer p-4 rounded-2xl border border-white/10 hover:border-white/20 transition-colors">
                        <input
                            data-testid="delete-confirm"
                            type="checkbox"
                            checked={form.confirm}
                            onChange={set("confirm")}
                            className="mt-1 w-4 h-4 accent-primary"
                        />
                        <span className="text-sm text-white/90">
                            I understand this action is <strong>permanent</strong> and cannot be
                            undone. I want India Reels to delete my account and all associated data.
                        </span>
                    </label>

                    {err && (
                        <div
                            data-testid="delete-error"
                            className="text-primary text-sm bg-primary/10 px-4 py-3 rounded-xl"
                        >
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || !form.confirm}
                        data-testid="delete-submit"
                        className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <TrashIcon size={18} weight="bold" />
                        {loading ? "Submitting..." : "Request account deletion"}
                    </button>
                </form>
            )}

            <p className="mt-8 text-xs text-text-secondary text-center">
                Need help? Email{" "}
                <a className="text-primary" href="mailto:privacy@india-reels.com">
                    privacy@india-reels.com
                </a>
            </p>
        </div>
    );
}

function Field({ label, testid, ...props }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-secondary font-bold">{label}</label>
            <input data-testid={testid} className="input-field mt-2" {...props} />
        </div>
    );
}
